import { createElement } from "react";
import type { FieldError } from "@shared-core/domain/object-ui/contract";
import type { CustomWidget, CustomWidgetProps } from "./types";

/** Is this error on one of these body paths (or beneath one)? */
export function isOnPaths(
  error: FieldError,
  paths: readonly string[],
): boolean {
  return paths.some(
    (p) => error.field === p || error.field.startsWith(`${p}.`),
  );
}

/**
 * One command's custom widgets as one (the registry holds one per command): an
 * item's configuration with its delivery allocation. Each draws in turn with
 * the shell's props, given only the errors on its own paths. They must draw in
 * the same place and own different paths: either mistake throws at start-up,
 * naming it.
 */
export function composeWidgets(...widgets: CustomWidget[]): CustomWidget {
  if (widgets.length === 0)
    throw new Error("composeWidgets: give it the widgets to compose.");
  const placements = [
    ...new Set(widgets.map((w) => w.placement ?? "before_fields")),
  ];
  if (placements.length > 1)
    throw new Error(
      `composeWidgets: the widgets draw in different places (${placements.join(", ")}); one command's widgets draw in one.`,
    );
  const ownedPaths = widgets.flatMap((w) => w.ownedPaths);
  const twice = ownedPaths.find((p, n) => ownedPaths.indexOf(p) !== n);
  if (twice !== undefined)
    throw new Error(
      `composeWidgets: two widgets own "${twice}"; each body path has one owner.`,
    );

  function ComposedWidgets(props: CustomWidgetProps) {
    return createElement(
      "div",
      { className: "space-y-4" },
      widgets.map(({ Component, ownedPaths: own }, n) =>
        createElement(Component, {
          key: n,
          ...props,
          errors: props.errors.filter((e) => isOnPaths(e, own)),
        }),
      ),
    );
  }
  return {
    ownedPaths,
    Component: ComposedWidgets,
    placement: placements[0]!,
  };
}
